import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { COLORS, SPACING } from '../styles/constants';
import TransparentLogo from './TransparentLogo';
import { FaBars, FaTimes } from 'react-icons/fa';

const HeaderWrapper = styled.div`
  background-color: transparent;
  position: absolute;
  width: 100%;
  z-index: 10;
`;

const HeaderContainer = styled.header`
  padding: ${SPACING.xs} ${SPACING.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: ${SPACING.xs} ${SPACING.sm};
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  z-index: 20;
`;

const NavContainer = styled.nav`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${props => props.$isOpen ? '0' : '-100%'};
    height: 100vh;
    width: 100%;
    background-color: ${COLORS.dark};
    flex-direction: column;
    justify-content: center;
    transition: right 0.3s ease-in-out;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: ${SPACING.lg};
  margin-right: ${SPACING.lg};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    margin-right: 0;
    margin-bottom: ${SPACING.xl};
  }
`;

const NavLink = styled(Link)`
  color: ${COLORS.white};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  padding: ${SPACING.xs} ${SPACING.sm};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  
  &:hover, &.active {
    color: ${COLORS.primary};
    
    &:after {
      transform: scaleX(1);
    }
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${COLORS.primary};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: ${SPACING.sm} ${SPACING.md};
  }
`;

const CTAButton = styled(Link)`
  background-color: ${COLORS.primary};
  color: ${COLORS.white};
  padding: ${SPACING.xs} ${SPACING.lg};
  border-radius: 4px;
  text-decoration: none;
  font-weight: bold;
  font-size: 1rem;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  
  &:hover {
    background-color: #0090C0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: ${SPACING.sm} ${SPACING.xl};
    font-size: 1.1rem;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${COLORS.white};
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 20;
  padding: ${SPACING.xs};
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleScroll = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const targetElement = document.getElementById(id);
        if (targetElement) {
          const offset = targetElement.offsetTop - 100;
          window.scrollTo({
            top: offset,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else if (element) {
      const offset = element.offsetTop - 100;
      window.scrollTo({
        top: offset,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  const handleContactClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <HeaderWrapper>
      <HeaderContainer>
        <LogoContainer to="/">
          <TransparentLogo />
        </LogoContainer>
        <MenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <FaTimes /> : <FaBars />}
        </MenuButton>
        <NavContainer $isOpen={isMenuOpen}>
          <NavLinks>
            <NavLink to="/" onClick={() => setIsMenuOpen(false)}>Home</NavLink>
            <NavLink to="/#services" onClick={(e) => handleScroll(e, 'services')}>Services</NavLink>
            <NavLink to="/#industries" onClick={(e) => handleScroll(e, 'industries')}>Industries</NavLink>
            <NavLink to="/#about" onClick={(e) => handleScroll(e, 'about')}>About Us</NavLink>
          </NavLinks>
          <CTAButton to="/contact" onClick={handleContactClick}>Contact Us</CTAButton>
        </NavContainer>
      </HeaderContainer>
    </HeaderWrapper>
  );
};

export default Header; 